import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import vendorApi from '../../services/vendorApi';
import { Search, Calendar, Loader2, CheckCircle, XCircle, Ban } from 'lucide-react';
import { canCancelApprovedStayBooking } from '../../utils/stayBookingStatus';
import { AdminPageHeader, adminCardClass, ADMIN_ICON_STROKE } from '../../components/admin/AdminShared';

const VendorBookings = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('approvalPending');
  const [searchQuery, setSearchQuery] = useState('');
  const [bookings, setBookings] = useState({
    approvalPending: [],
    upcoming: [],
    inhouse: [],
    completed: []
  });
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const loadBookings = useCallback(async () => {
    if (!user || (user.role !== 'vendor' && user.role !== 'admin')) return;
    try {
      setFetchLoading(true);
      setFetchError('');
      const data = await vendorApi.getBookings();
      setBookings({
        approvalPending: data.approvalPending || [],
        upcoming: data.upcoming || [],
        inhouse: data.inhouse || [],
        completed: data.completed || []
      });
    } catch (e) {
      setFetchError(e.response?.data?.error || 'Could not load bookings');
      setBookings({ approvalPending: [], upcoming: [], inhouse: [], completed: [] });
    } finally {
      setFetchLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated || !user) {
        navigate('/vendor/login', { replace: true });
        return;
      }
      if (user.role !== 'vendor' && user.role !== 'admin') {
        navigate('/vendor/login', { replace: true, state: { needsVendorAccount: true } });
        return;
      }
    }
  }, [user, loading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await loadBookings();
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loadBookings]);

  const handleApprove = async (bookingId) => {
    try {
      setApprovingId(bookingId);
      await vendorApi.approveBooking(bookingId);
      await loadBookings();
      if (activeTab === 'approvalPending') {
        const remaining = bookings.approvalPending.filter((b) => b.bookingId !== bookingId);
        if (remaining.length === 0) {
          setActiveTab('upcoming');
        }
      }
    } catch (e) {
      setFetchError(e.response?.data?.error || 'Could not approve booking');
    } finally {
      setApprovingId(null);
    }
  };

  const handleCancelApproved = async (bookingId) => {
    const confirmed = window.confirm(
      'Cancel this approved booking?\n\nThis cannot be undone. The guest will see the booking as cancelled and the reserved room nights will be returned to your inventory.'
    );
    if (!confirmed) return;

    try {
      setCancellingId(bookingId);
      setFetchError('');
      await vendorApi.cancelApprovedBooking(bookingId);
      await loadBookings();
    } catch (e) {
      setFetchError(e.response?.data?.error || 'Could not cancel booking');
    } finally {
      setCancellingId(null);
    }
  };

  const handleDecline = async (bookingId) => {
    const confirmed = window.confirm(
      'Decline this booking request?\n\nThis cannot be undone. The guest will see the booking as declined and the reserved room nights will be released back to your inventory.'
    );
    if (!confirmed) return;

    try {
      setDecliningId(bookingId);
      setFetchError('');
      await vendorApi.declineBooking(bookingId);
      await loadBookings();
      if (activeTab === 'approvalPending') {
        const remaining = bookings.approvalPending.filter((b) => b.bookingId !== bookingId);
        if (remaining.length === 0) {
          setActiveTab('upcoming');
        }
      }
    } catch (e) {
      setFetchError(e.response?.data?.error || 'Could not decline booking');
    } finally {
      setDecliningId(null);
    }
  };

  const currentBookings = bookings[activeTab] || [];

  const filteredBookings = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return currentBookings;
    return currentBookings.filter(
      (b) =>
        (b.customerName && b.customerName.toLowerCase().includes(q)) ||
        (b.phone && String(b.phone).toLowerCase().includes(q)) ||
        (b.stayTitle && b.stayTitle.toLowerCase().includes(q)) ||
        (b.bookingId && b.bookingId.toLowerCase().includes(q))
    );
  }, [currentBookings, searchQuery]);

  const displayName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'Vendor';

  if (loading || !isAuthenticated || !user || (user.role !== 'vendor' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'approvalPending',
      label: 'Approval requests',
      count: bookings.approvalPending.length
    },
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'inhouse', label: 'In-house' },
    { id: 'completed', label: 'Completed' }
  ];

  return (
    <div>
      <AdminPageHeader title="Bookings" subtitle={`Signed in as ${displayName}.`} />

      <div className={`${adminCardClass} p-4 md:p-5 mb-6`}>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78736A]" strokeWidth={ADMIN_ICON_STROKE} />
          <input
            type="search"
            placeholder="Search by guest, phone, stay, booking id…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-[#E5E3DF] rounded-[12px] bg-white text-black text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
            autoComplete="off"
          />
        </div>

        <div className="flex gap-2 flex-wrap border-t border-[#E5E3DF] pt-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors inline-flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'bg-black text-white'
                  : 'bg-[#FAF7F2] text-black border border-[#E5E3DF] hover:bg-[#f5f0e8]'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span
                  className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-white/20' : 'bg-[#FF5E4C] text-white'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {fetchError && (
          <div className="p-3 rounded-[12px] bg-red-50 text-red-800 text-sm border border-red-100">{fetchError}</div>
        )}

        {fetchLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 text-[#FF5E4C] animate-spin" strokeWidth={ADMIN_ICON_STROKE} />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className={`${adminCardClass} text-center py-12`}>
            <Calendar className="w-14 h-14 text-[#E5E3DF] mx-auto mb-4" strokeWidth={ADMIN_ICON_STROKE} />
            <p className="text-[#78736A]">
              {searchQuery.trim()
                ? 'No bookings match your search.'
                : activeTab === 'approvalPending'
                  ? 'No bookings awaiting your approval.'
                  : `No ${activeTab} bookings yet.`}
            </p>
          </div>
        ) : (
          filteredBookings.map((booking) => (
            <div key={booking.bookingId} className={`${adminCardClass} p-4 md:p-5`}>
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="min-w-0">
                  <h3 className="font-medium text-black truncate text-[15px]">
                    {booking.customerName}
                    {booking.guests != null ? ` · ${booking.guests} guest(s)` : ''}
                  </h3>
                  <p className="text-[12px] text-[#78736A] truncate mt-0.5">{booking.stayTitle}</p>
                  <p className="text-[13px] text-[#5c5c5c] mt-1">{booking.phone || '—'}</p>
                  <p className="text-[11px] text-[#78736A] mt-1 font-mono">{booking.bookingId}</p>
                </div>
                <p className="text-lg font-medium text-[#FF5E4C] shrink-0 tabular-nums">
                  {booking.amountFormatted || '—'}
                </p>
              </div>
              <div className="flex items-center text-[13px] text-[#5c5c5c]">
                <Calendar className="w-4 h-4 mr-2 shrink-0 text-[#FF5E4C]" strokeWidth={ADMIN_ICON_STROKE} />
                <span>
                  {booking.checkInLabel || '—'} → {booking.checkOutLabel || '—'}
                </span>
              </div>
              {activeTab === 'approvalPending' ? (
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wide text-amber-800 bg-amber-50 px-2 py-1 rounded-full border border-amber-100">
                    Awaiting approval
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleDecline(booking.bookingId)}
                      disabled={
                        decliningId === booking.bookingId || approvingId === booking.bookingId
                      }
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#EF4444] border border-[#FECACA] text-[13px] font-medium hover:bg-red-50 disabled:opacity-50"
                    >
                      {decliningId === booking.bookingId ? (
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={ADMIN_ICON_STROKE} />
                      ) : (
                        <XCircle className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      )}
                      Decline
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApprove(booking.bookingId)}
                      disabled={
                        approvingId === booking.bookingId || decliningId === booking.bookingId
                      }
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#10B981] text-white text-[13px] font-medium hover:bg-[#059669] disabled:opacity-50"
                    >
                      {approvingId === booking.bookingId ? (
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={ADMIN_ICON_STROKE} />
                      ) : (
                        <CheckCircle className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      )}
                      Approve
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wide text-[#78736A] bg-[#FAF7F2] px-2 py-1 rounded-full border border-[#E5E3DF]">
                    {booking.status === 'COMPLETED' ? 'Approved' : booking.status}
                  </span>
                  {(activeTab === 'upcoming' || activeTab === 'inhouse') &&
                    canCancelApprovedStayBooking(booking) && (
                      <button
                        type="button"
                        onClick={() => handleCancelApproved(booking.bookingId)}
                        disabled={
                          cancellingId === booking.bookingId ||
                          approvingId === booking.bookingId ||
                          decliningId === booking.bookingId
                        }
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#78736A] border border-[#E5E3DF] text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50"
                      >
                        {cancellingId === booking.bookingId ? (
                          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={ADMIN_ICON_STROKE} />
                        ) : (
                          <Ban className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                        )}
                        Cancel booking
                      </button>
                    )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default VendorBookings;
